import { clearTokens, getAccess, getRefresh, setTokens } from './auth';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorBody {
  code?: string;
  message?: string;
  error?: string;
  details?: unknown;
}

/**
 * Exported because the agent's streaming turn cannot go through `api()`.
 * `api()` reads the whole body as JSON before it returns, which is the one
 * thing a stream must not do — so useAgentChat issues a raw fetch and needs the
 * same base URL rather than a second, silently diverging copy of it.
 */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/erp';

/**
 * Single in-flight refresh promise so N parallel 401s only trigger ONE refresh.
 * Resolves to the new access token, or null if refresh failed.
 */
let refreshing: Promise<string | null> | null = null;

/**
 * A usable access token, refreshing first if the current one has expired.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS EXPORTED
 *
 * `api()` handles a 401 by refreshing and retrying, and every JSON call goes
 * through it. The agent's streaming call does not and cannot — it reads an
 * NDJSON body incrementally while `api()` parses the whole response as JSON
 * before returning — so it was written as a raw fetch with whatever token
 * happened to be in storage.
 *
 * Access tokens live fifteen minutes. Anybody working for longer than that,
 * which is everybody, eventually sent a message on a dead token: the stream
 * 401'd or was cut mid-body (ERR_INCOMPLETE_CHUNKED_ENCODING), and the panel
 * showed "Something went wrong. Try again." Trying again failed the same way,
 * because nothing about the expiry had changed.
 *
 * The refresh machinery was there the whole time. It simply was not reachable
 * from the one call that needed it most.
 */
export async function freshAccessToken(): Promise<string | null> {
  const token = getAccess();
  if (token && !isExpired(token)) return token;
  return tryRefresh();
}

/**
 * Is this JWT past its expiry? Read locally, with a small margin.
 *
 * The margin matters for a stream: a token with four seconds left passes a
 * naive check, and then dies in the middle of the answer — which is the worst
 * moment, because the customer has already watched half of it arrive.
 */
function isExpired(token: string, marginSeconds = 60): boolean {
  try {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    if (!exp) return false;
    return Date.now() / 1000 >= exp - marginSeconds;
  } catch {
    return false; // unreadable — let the server be the judge
  }
}

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const attempt = async (token: string): Promise<string | null> => {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: token }),
      });
      if (!res.ok) return null;
      const j = (await res.json()) as { access?: string; refresh?: string | null };
      if (!j.access) return null;
      setTokens(j.access, j.refresh ?? null);
      return j.access;
    };
    const refresh = getRefresh();
    if (!refresh) return null;
    try {
      const first = await attempt(refresh);
      if (first) return first;
      // Another tab may have rotated the shared refresh token between our read
      // and this call. Re-read once; if it changed, retry with the fresh token
      // so a benign concurrent rotation doesn't force a spurious logout.
      const latest = getRefresh();
      if (latest && latest !== refresh) {
        return await attempt(latest);
      }
      return null;
    } catch {
      return null;
    }
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

function fullUrl(path: string): string {
  return path.startsWith('http') || path.startsWith('/api')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The session is gone. Send them to sign in again, and remember where they were.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SENT PEOPLE TO /erp/login, WHICH IS NOT A PAGE
 *
 * The app is mounted at /app. There is no /erp/login and there has not been for
 * as long as anyone can remember: it is a leftover from an older mount point.
 * nginx has no route for it either, so the marketing catch-all answered and the
 * customer, mid-task, was handed the sales homepage with no explanation and no
 * way back to what they were doing.
 *
 * It fires on every expired session, which is every time somebody comes back
 * after a break. It is why "I open the admin link and it takes me somewhere
 * else" was reported four times while I kept proving the redirect, the route,
 * the bundle and the API were all correct. They were. This line was throwing
 * the person out one layer below all of them.
 *
 * Found by loading the page in a real browser with a session that had just
 * expired, which is the one thing none of the checks had ever done.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A HARD NAVIGATION, AND WHY THE DESTINATION TRAVELS IN THE QUERY
 *
 * This module has no router: it is the fetch layer, and it can be called from
 * anywhere including a background refresh. So it navigates the browser rather
 * than the router, and the page it is leaving is recorded in the URL because a
 * hard navigation destroys history.state on the way.
 */
const LOGIN_PATH = '/app/login';

async function attemptLogout(): Promise<void> {
  clearTokens();
  if (typeof window === 'undefined') return;
  if (window.location.pathname.endsWith('/login')) return;

  /* Router paths are relative to the /app basename, so it is stripped here and
     put back by the router when it navigates. */
  const here = window.location.pathname.replace(/^\/app/, '') + window.location.search;
  const target = here && here !== '/' ? `${LOGIN_PATH}?from=${encodeURIComponent(here)}` : LOGIN_PATH;
  window.location.replace(target);
}

/**
 * `RequestInit`, except `body` may be a plain object.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TYPE HAD TO BE WIDENED
 *
 * `api()` has always accepted an object and serialised it, setting the JSON
 * Content-Type on the way, which is the part that matters. But `RequestInit.body`
 * is `BodyInit`, which does not include a plain object, so passing one is a type
 * error and the obvious way out is `JSON.stringify(...)`.
 *
 * That "fix" compiles and then silently fails: a body that is ALREADY a string
 * skips the branch below, no Content-Type is set, `express.json()` declines to
 * parse it, and the server receives an empty object. The endpoint answers 400
 * complaining that a required field is missing, a field the browser plainly
 * sent. It took a headless browser and a leave request that would not save to
 * find, because nothing about it looks wrong in either file.
 *
 * So the type now says what the function always did. Passing a string still
 * works for the callers that also set the header themselves.
 */
export type ApiInit = Omit<RequestInit, 'body'> & {
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
};

export async function api<T>(path: string, init: ApiInit = {}, retried = false): Promise<T> {
  const url = fullUrl(path);
  const headers = new Headers(init.headers);

  const access = getAccess();
  if (access && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${access}`);
  }

  let body = init.body as BodyInit | null | undefined;
  if (body !== undefined && body !== null && !(body instanceof FormData) && typeof body === 'object') {
    body = JSON.stringify(body);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers, body });
  } catch (cause) {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network request failed', cause);
  }

  if (response.status === 401) {
    // Try to refresh exactly once before giving up. If the refresh succeeds,
    // re-issue the original request transparently — caller never sees a 401.
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) {
        return api<T>(path, init, true);
      }
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    let payload: ErrorBody = {};
    if (isJson) {
      try {
        payload = (await response.json()) as ErrorBody;
      } catch {
        payload = {};
      }
    } else {
      try {
        payload = { message: await response.text() };
      } catch {
        payload = {};
      }
    }
    throw new ApiError(
      response.status,
      payload.code ?? `HTTP_${response.status}`,
      payload.message ?? payload.error ?? response.statusText ?? 'Request failed',
      payload.details,
    );
  }

  if (response.status === 204) return undefined as T;
  if (isJson) return (await response.json()) as T;
  return (await response.text()) as unknown as T;
}

// Convenience helpers — accept JSON body shorthand.
api.get = <T>(path: string): Promise<T> => api<T>(path);
api.post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: body as BodyInit | undefined });
api.patch = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: body as BodyInit | undefined });
api.delete = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' });

/**
 * POSTs to a PDF endpoint, reads the binary blob, and opens it in a new tab.
 * Falls back to a download anchor if popups are blocked.
 */
export async function downloadPdf(
  path: string,
  filename: string,
  body?: Record<string, unknown>,
  retried = false,
): Promise<void> {
  const url = fullUrl(path);
  const headers = new Headers();
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  let payloadBody: BodyInit | undefined;
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payloadBody = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, { method: 'POST', headers, body: payloadBody });
  } catch (cause) {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network request failed', cause);
  }

  if (response.status === 401) {
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) return downloadPdf(path, filename, body, true);
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }

  if (!response.ok) {
    let payload: ErrorBody = {};
    try {
      payload = (await response.json()) as ErrorBody;
    } catch {
      payload = {};
    }
    throw new ApiError(
      response.status,
      payload.code ?? `HTTP_${response.status}`,
      payload.message ?? payload.error ?? response.statusText ?? 'Request failed',
      payload.details,
    );
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const win = typeof window !== 'undefined' ? window.open(objectUrl, '_blank', 'noopener') : null;
  if (!win && typeof document !== 'undefined') {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  if (typeof window !== 'undefined') {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}

/**
 * POSTs to a PDF endpoint, returns a temporary object URL for inline preview
 * (e.g. inside an <iframe>). Caller must revoke when done.
 */
export async function fetchPdfBlobUrl(
  path: string,
  body?: Record<string, unknown>,
  retried = false,
  method: 'GET' | 'POST' = 'POST',
): Promise<string> {
  const url = fullUrl(path);
  const headers = new Headers();
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  let payloadBody: BodyInit | undefined;
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payloadBody = JSON.stringify(body);
  }

  /*
    THE METHOD IS TOLD, NOT INFERRED.

    This was always POST — right for the document routes (an invoice, a payslip,
    a contract) and wrong for every REPORT route, which are GETs taking their
    arguments in the query string. A report the agent produced came back 404 and
    the panel told the customer "Could not open this document" about a file that
    existed and was correct.

    The first fix was "no body means GET". It would have broken every preview
    button in the product: they all call this with a path and no body against
    POST routes. The body is not evidence of the method. So the caller says,
    and the default stays POST so nothing that works today changes.
  */
  const response = await fetch(url, { method, headers, body: payloadBody });

  if (response.status === 401) {
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) return fetchPdfBlobUrl(path, body, true, method);
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }
  if (!response.ok) {
    let p: ErrorBody = {};
    try { p = (await response.json()) as ErrorBody; } catch { /* ignore */ }
    throw new ApiError(response.status, p.code ?? `HTTP_${response.status}`, p.message ?? p.error ?? 'Request failed');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * GET a binary document and hand back an object URL for it.
 *
 * Separate from `fetchPdfBlobUrl` for two reasons that both matter to the
 * document preview in a detail pane:
 *
 *   • It is ABORTABLE. Arrowing down a list mounts and unmounts one preview per
 *     row, and without a signal every one of those keeps its request alive to
 *     completion, holding a socket and a slot in the server's render queue for
 *     a picture nobody is waiting for any more.
 *   • It does not touch the cache mode. These endpoints answer with an ETag and
 *     `Cache-Control: private, no-cache`, so the browser stores the bytes and
 *     revalidates: the second view of the same document is a 304 and a read
 *     from disk. Passing `cache: 'no-store'` here, which looks like the safe
 *     thing to write, would throw that away and re-download every time.
 *
 * The caller owns the returned URL and must `URL.revokeObjectURL` it.
 */
export async function fetchDocumentBlobUrl(
  path: string,
  signal?: AbortSignal,
  retried = false,
): Promise<string> {
  const url = fullUrl(path);
  const headers = new Headers();
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const response = await fetch(url, { method: 'GET', headers, signal });

  if (response.status === 401) {
    if (!retried) {
      const newAccess = await tryRefresh();
      if (newAccess) return fetchDocumentBlobUrl(path, signal, true);
    }
    await attemptLogout();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired');
  }

  if (!response.ok) {
    let p: ErrorBody = {};
    try {
      p = (await response.json()) as ErrorBody;
    } catch {
      /* a render failure can answer with something that is not JSON */
    }
    throw new ApiError(
      response.status,
      p.code ?? `HTTP_${response.status}`,
      p.message ?? p.error ?? 'Request failed',
    );
  }

  return URL.createObjectURL(await response.blob());
}
